import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Brain, Target, TrendingUp, Calendar, CheckCircle2, 
  Clock, BookOpen, Zap, Award, ChevronRight, Play, BarChart3,
  Lightbulb, AlertCircle, Loader2, Star, Trophy, Sparkles, MessageCircle,
  Library, Route, RefreshCw, ArrowRight, FileText, Check
} from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Year group to stage mapping
const YEAR_TO_STAGE = {
  'year-1': 'ks1', 'year-2': 'ks1',
  'year-3': 'ks2-lower', 'year-4': 'ks2-lower',
  'year-5': 'ks2-upper', 'year-6': 'ks2-upper',
  'year-7': 'ks3', 'year-8': 'ks3', 'year-9': 'ks3',
  'year-10': 'ks4', 'year-11': 'ks4',
  'year-12': 'a-level', 'year-13': 'a-level',
  '7-plus': '7-plus', '11-plus': '11-plus',
  'gcse': 'ks4', 'a-level': 'a-level'
};

// Helper to get stage from year group
const getStageFromYearGroup = (yearGroup) => {
  if (!yearGroup) return 'ks3'; // Default to KS3
  const normalized = yearGroup.toLowerCase().replace(/\s+/g, '-');
  return YEAR_TO_STAGE[normalized] || 'ks3';
};

// Capability band colors
const BAND_COLORS = {
  foundation: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50' },
  developing: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/50' },
  secure: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50' },
  stretch: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/50' }
};

// Route descriptions
const LEARNING_ROUTES = {
  A: {
    name: 'Route A: Personalised Adaptive Path',
    shortName: 'Adaptive Path',
    icon: Brain,
    color: 'from-purple-500 to-pink-500',
    description: 'AI creates a personalised weekly plan that adapts to your progress',
    features: ['Daily micro-lessons', 'AI difficulty adjustment', 'Weekly re-optimisation', 'Grade forecasting'],
    bestFor: 'Students who want structured, guided learning with continuous improvement'
  },
  B: {
    name: 'Route B: Year Group Practice Library',
    shortName: 'Practice Library',
    icon: Library,
    color: 'from-blue-500 to-cyan-500',
    description: 'Access complete curriculum packs with 100+ practice papers',
    features: ['Full topic worksheets', 'Mock exam papers', 'Instant analytics', 'Self-paced study'],
    bestFor: 'Students who prefer structured exam exposure and self-directed practice'
  }
};

const LearningEngine = () => {
  const navigate = useNavigate();
  const { stage, subject } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard, diagnostic, lesson, practice, ai-help, route-choice, weekly-summary
  const [learningPath, setLearningPath] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null); // 'A' or 'B'
  const [recommendedRoute, setRecommendedRoute] = useState(null);
  
  // Diagnostic state
  const [diagnosticSession, setDiagnosticSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [diagnosticProgress, setDiagnosticProgress] = useState(0);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  
  // Weekly summary state
  const [weeklySummary, setWeeklySummary] = useState(null);
  
  // Lesson state
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonTab, setLessonTab] = useState('learn');
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [practiceSubmitted, setPracticeSubmitted] = useState(false);
  const [practiceResults, setPracticeResults] = useState(null);
  
  // AI Help state
  const [aiExplanation, setAiExplanation] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [strugglingTopics, setStrugglingTopics] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadLearningPath();
  }, [stage, subject]);

  const loadLearningPath = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/learning-engine/path/current`, { headers });
      
      if (response.data.has_path) {
        setLearningPath(response.data.path);
        setView('dashboard');
      } else {
        setView('start');
      }
    } catch (error) {
      console.error('Error loading learning path:', error);
      setView('start');
    }
    setLoading(false);
  };

  // Calculate route recommendation based on diagnostic results
  const calculateRecommendedRoute = (results) => {
    // Route A (Adaptive) recommended for: 
    // - Students with clear weak areas that need targeted improvement
    // - Students scoring below 70% who need structured guidance
    // Route B (Practice Library) recommended for:
    // - Students scoring above 70% who may prefer self-directed practice
    // - Students with relatively balanced performance across topics
    
    const score = results.score;
    const weakTopicsCount = results.weak_topics?.length || 0;
    
    if (score < 70 || weakTopicsCount >= 2) {
      return 'A'; // Adaptive path for students needing more support
    } else {
      return 'B'; // Practice library for stronger students
    }
  };

  const startDiagnostic = async () => {
    try {
      setLoading(true);
      
      // Get user's year group from localStorage or profile
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const yearGroup = user.year_group || user.yearGroup;
      
      // Determine correct stage from year group (not URL param)
      const effectiveStage = yearGroup 
        ? getStageFromYearGroup(yearGroup) 
        : (stage || 'ks3');
      
      const response = await axios.post(`${API}/learning-engine/diagnostic/start`, {
        stage: effectiveStage,
        subject: subject || 'maths'
      }, { headers });
      
      setDiagnosticSession(response.data.session_id);
      setCurrentQuestion(response.data.current_question);
      setDiagnosticProgress(0);
      setView('diagnostic');
      toast.success('Diagnostic started! Answer each question carefully.');
    } catch (error) {
      toast.error('Failed to start diagnostic');
      console.error(error);
    }
    setLoading(false);
  };

  const submitDiagnosticAnswer = async () => {
    if (selectedAnswer === null) {
      toast.error('Please select an answer');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/learning-engine/diagnostic/answer`, {
        session_id: diagnosticSession,
        question_index: currentQuestion.index,
        selected_answer: selectedAnswer,
        time_taken: 30 // Simplified - could track actual time
      }, { headers });

      if (response.data.completed) {
        setDiagnosticResults(response.data.results);
        // Calculate and set recommended route
        const recommended = calculateRecommendedRoute(response.data.results);
        setRecommendedRoute(recommended);
        setView('results');
        toast.success('Diagnostic complete!');
      } else {
        setCurrentQuestion(response.data.next_question);
        setDiagnosticProgress(response.data.progress);
        setSelectedAnswer(null);
      }
    } catch (error) {
      toast.error('Failed to submit answer');
      console.error(error);
    }
    setLoading(false);
  };

  const generateLearningPath = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/learning-engine/path/generate`, {
        diagnostic_session_id: diagnosticSession
      }, { headers });

      toast.success('Your personalized learning path is ready!');
      await loadLearningPath();
    } catch (error) {
      toast.error('Failed to generate learning path');
      console.error(error);
    }
    setLoading(false);
  };

  const openLesson = async (lessonId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/learning-engine/lesson/${lessonId}`, { headers });
      setCurrentLesson(response.data.lesson);
      setLessonTab('learn');
      setPracticeAnswers({});
      setPracticeSubmitted(false);
      setPracticeResults(null);
      setView('lesson');
    } catch (error) {
      toast.error('Failed to load lesson');
      console.error(error);
    }
    setLoading(false);
  };

  const submitPractice = async () => {
    if (!currentLesson) return;

    const results = currentLesson.practice_questions.map((q, idx) => ({
      question_index: idx,
      selected_answer: practiceAnswers[idx] ?? -1,
      correct: practiceAnswers[idx] === q.correct_answer,
      time_taken: 20
    }));

    try {
      setLoading(true);
      const response = await axios.post(`${API}/learning-engine/lesson/complete`, {
        lesson_id: currentLesson.topic_id,
        practice_results: results
      }, { headers });

      setPracticeResults(response.data);
      setPracticeSubmitted(true);
      toast.success(response.data.message);
      
      // Reload learning path to update progress
      await loadLearningPath();
    } catch (error) {
      toast.error('Failed to submit practice');
      console.error(error);
    }
    setLoading(false);
  };

  const markActivityComplete = async (activityId) => {
    try {
      await axios.post(`${API}/learning-engine/activity/update`, {
        activity_id: activityId,
        completed: true
      }, { headers });
      
      toast.success('Activity marked complete!');
      await loadLearningPath();
    } catch (error) {
      toast.error('Failed to update activity');
    }
  };

  // Get AI explanation for struggling topic
  const getAIExplanation = async (topic, mistakes = []) => {
    try {
      setAiLoading(true);
      setAiExplanation(null);
      
      const response = await axios.post(`${API}/learning-engine/ai/explain`, {
        topic: topic,
        stage: learningPath?.stage || stage || '11-plus',
        subject: learningPath?.subject || subject || 'maths',
        student_mistakes: mistakes,
        difficulty_level: learningPath?.current_band || 'developing'
      }, { headers });
      
      setAiExplanation(response.data);
      setView('ai-help');
      toast.success(`AI explanation generated using ${response.data.provider}`);
    } catch (error) {
      console.error('AI explanation error:', error);
      toast.error('Failed to generate AI explanation');
    }
    setAiLoading(false);
  };

  // Load struggling topics
  const loadStrugglingTopics = async () => {
    try {
      const response = await axios.get(`${API}/learning-engine/ai/struggling-topics`, { headers });
      setStrugglingTopics(response.data.struggling_topics || []);
    } catch (error) {
      console.error('Failed to load struggling topics:', error);
    }
  };

  // Load struggling topics when dashboard loads
  useEffect(() => {
    if (learningPath) {
      loadStrugglingTopics();
    }
  }, [learningPath]);

  // Loading state
  if (loading && view === 'dashboard') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  // AI Help view
  if (view === 'ai-help') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setView('dashboard')}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white">AI Tutor Explanation</CardTitle>
                  <CardDescription className="text-gray-400">
                    Personalized help for: {aiExplanation?.topic}
                  </CardDescription>
                </div>
              </div>
              {aiExplanation?.provider && (
                <Badge className="w-fit mt-2 bg-purple-500/20 text-purple-400">
                  Powered by {aiExplanation.provider === 'openai' ? 'GPT-4' : 
                              aiExplanation.provider === 'gemini' ? 'Gemini' : 
                              aiExplanation.provider === 'anthropic' ? 'Claude' : 'AI'}
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {aiLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                  <span className="ml-3 text-gray-400">Generating personalized explanation...</span>
                </div>
              ) : aiExplanation?.explanation ? (
                <div className="prose prose-invert max-w-none">
                  <div className="bg-slate-700/30 rounded-lg p-6 text-gray-200">
                    <ReactMarkdown
                      components={{
                        h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-white mb-4" {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-xl font-semibold text-purple-400 mt-6 mb-3" {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-lg font-semibold text-white mt-4 mb-2" {...props} />,
                        p: ({node, ...props}) => <p className="text-gray-300 mb-3 leading-relaxed" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-1 mb-4" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal list-inside space-y-1 mb-4" {...props} />,
                        li: ({node, ...props}) => <li className="text-gray-300" {...props} />,
                        strong: ({node, ...props}) => <strong className="text-white font-semibold" {...props} />,
                        code: ({node, ...props}) => <code className="bg-slate-800 px-1 rounded text-purple-400" {...props} />,
                      }}
                    >
                      {aiExplanation.explanation}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">No explanation available</p>
              )}
              
              <div className="mt-6 flex gap-3">
                <Button 
                  onClick={() => setView('dashboard')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Learning
                </Button>
                <Button 
                  onClick={() => getAIExplanation(aiExplanation?.topic, [])}
                  className="flex-1 bg-purple-500 hover:bg-purple-600"
                  disabled={aiLoading}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Explain Again
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Start view - No learning path yet
  if (view === 'start') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </Button>

          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <CardTitle className="text-3xl text-white">Eds SmartPath™</CardTitle>
              <CardDescription className="text-gray-400 text-lg italic">
                "My Personal Learning Planner"
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Target className="w-8 h-8 mx-auto text-blue-400 mb-2" />
                  <h3 className="font-semibold text-white">Diagnostic</h3>
                  <p className="text-sm text-gray-400">15 questions to assess your level</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <Calendar className="w-8 h-8 mx-auto text-green-400 mb-2" />
                  <h3 className="font-semibold text-white">Weekly Plan</h3>
                  <p className="text-sm text-gray-400">Personalized daily activities</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <TrendingUp className="w-8 h-8 mx-auto text-purple-400 mb-2" />
                  <h3 className="font-semibold text-white">Grade Forecast</h3>
                  <p className="text-sm text-gray-400">Track progress & predictions</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg p-6 border border-purple-500/30">
                <h3 className="text-xl font-bold text-white mb-2">Ready to start?</h3>
                <p className="text-gray-300 mb-4">
                  Take a quick 15-question diagnostic to create your personalized learning path.
                  This will identify your strengths and areas to improve.
                </p>
                <Button 
                  onClick={startDiagnostic}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                  Start Diagnostic Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Diagnostic view
  if (view === 'diagnostic' && currentQuestion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardHeader>
              <div className="flex justify-between items-center mb-2">
                <Badge variant="outline" className="text-purple-400 border-purple-400">
                  Question {currentQuestion.index + 1} of 15
                </Badge>
                <Badge className={`${BAND_COLORS[currentQuestion.topic]?.bg || 'bg-blue-500/20'} ${BAND_COLORS[currentQuestion.topic]?.text || 'text-blue-400'}`}>
                  {currentQuestion.topic}
                </Badge>
              </div>
              <Progress value={diagnosticProgress} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <h2 className="text-xl font-semibold text-white">
                {currentQuestion.question}
              </h2>

              <RadioGroup 
                value={selectedAnswer?.toString()} 
                onValueChange={(v) => setSelectedAnswer(parseInt(v))}
                className="space-y-3"
              >
                {currentQuestion.options.map((option, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center space-x-3 p-4 rounded-lg border transition-all cursor-pointer
                      ${selectedAnswer === idx 
                        ? 'bg-purple-500/20 border-purple-500' 
                        : 'bg-slate-700/50 border-slate-600 hover:border-purple-500/50'}`}
                    onClick={() => setSelectedAnswer(idx)}
                  >
                    <RadioGroupItem value={idx.toString()} id={`option-${idx}`} />
                    <Label htmlFor={`option-${idx}`} className="text-white cursor-pointer flex-1">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <Button 
                onClick={submitDiagnosticAnswer}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                disabled={selectedAnswer === null || loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Answer
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Results view - Updated with Route Recommendation
  if (view === 'results' && diagnosticResults) {
    const bandColor = BAND_COLORS[diagnosticResults.overall_band] || BAND_COLORS.secure;
    const recommendedRouteInfo = LEARNING_ROUTES[recommendedRoute] || LEARNING_ROUTES.A;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardHeader className="text-center">
              <div className={`w-24 h-24 mx-auto mb-4 rounded-full ${bandColor.bg} ${bandColor.border} border-2 flex items-center justify-center`}>
                <Trophy className={`w-12 h-12 ${bandColor.text}`} />
              </div>
              <CardTitle className="text-2xl text-white">Diagnostic Complete!</CardTitle>
              <CardDescription className="text-gray-400">
                Here's your personalized assessment
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Score */}
              <div className="text-center">
                <div className="text-5xl font-bold text-white mb-2">{diagnosticResults.score}%</div>
                <Badge className={`${bandColor.bg} ${bandColor.text} text-lg px-4 py-1`}>
                  {diagnosticResults.overall_band.charAt(0).toUpperCase() + diagnosticResults.overall_band.slice(1)} Level
                </Badge>
              </div>

              {/* Topic Breakdown */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-white">Topic Performance</h3>
                {Object.entries(diagnosticResults.topic_performance).map(([topic, data]) => (
                  <div key={topic} className="bg-slate-700/50 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-white capitalize">{topic}</span>
                      <span className={BAND_COLORS[data.band]?.text || 'text-gray-400'}>{data.percentage}%</span>
                    </div>
                    <Progress value={data.percentage} className="h-2" />
                  </div>
                ))}
              </div>

              {/* Weak Areas */}
              {diagnosticResults.weak_topics.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <h3 className="font-semibold text-red-400">Focus Areas</h3>
                  </div>
                  <p className="text-gray-300 text-sm">
                    {diagnosticResults.weak_topics.join(', ')} - Your learning path will prioritize these topics
                  </p>
                </div>
              )}

              {/* Continue to Route Selection */}
              <Button 
                onClick={() => setView('route-choice')}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="continue-to-route-btn"
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Route Choice View - NEW (also handles route switching from existing path)
  if (view === 'route-choice') {
    // Use existing recommended route or default to 'A' if switching from dashboard
    const effectiveRecommendedRoute = recommendedRoute || (learningPath ? 'A' : 'A');
    const recommendedRouteInfo = LEARNING_ROUTES[effectiveRecommendedRoute] || LEARNING_ROUTES.A;
    const RecommendedIcon = recommendedRouteInfo.icon;
    const isRouteSwitching = learningPath && !diagnosticResults; // User is switching routes from dashboard
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => isRouteSwitching ? setView('dashboard') : setView('results')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {isRouteSwitching ? 'Back to Dashboard' : 'Back to Results'}
          </Button>

          {/* Route Switch Header (when switching from existing path) */}
          {isRouteSwitching && (
            <Card className="bg-gradient-to-r from-blue-600 to-cyan-600 border-0 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Route className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-white">Switch Your Learning Route</h2>
                    <p className="text-white/80 text-sm mt-1">
                      Choose a different learning approach. Your progress will be saved.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ed's Picks Banner (only after diagnostic) */}
          {diagnosticResults && (
            <Card className={`bg-gradient-to-r ${recommendedRouteInfo.color} border-0 shadow-xl`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-white/20 text-white border-0">Ed's Picks</Badge>
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      Based on your diagnostic, we recommend {recommendedRouteInfo.shortName}
                    </h2>
                    <p className="text-white/80 text-sm mt-1">{recommendedRouteInfo.bestFor}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Route Options */}
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(LEARNING_ROUTES).map(([routeKey, route]) => {
              const RouteIcon = route.icon;
              const isRecommended = routeKey === recommendedRoute;
              const isSelected = selectedRoute === routeKey;
              
              return (
                <Card 
                  key={routeKey}
                  className={`relative cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-2 border-purple-500 bg-purple-500/10' 
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'
                  }`}
                  onClick={() => setSelectedRoute(routeKey)}
                  data-testid={`route-${routeKey}-card`}
                >
                  {isRecommended && (
                    <div className="absolute -top-3 right-4">
                      <Badge className="bg-green-500 text-white">
                        <Sparkles className="w-3 h-3 mr-1" /> Recommended
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${route.color}`}>
                        <RouteIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-white">{route.name}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-400 text-sm">{route.description}</p>
                    
                    <div className="space-y-2">
                      {route.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {isSelected && (
                      <div className="flex items-center justify-center pt-2">
                        <Check className="w-6 h-6 text-purple-400" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button 
              onClick={async () => {
                if (selectedRoute === 'A') {
                  // If already have a path but switching, just go to dashboard
                  if (learningPath) {
                    toast.success('Continuing with Adaptive Path');
                    setView('dashboard');
                  } else {
                    generateLearningPath();
                  }
                } else {
                  // Route B - go to practice library
                  toast.success('Welcome to the Practice Library!');
                  navigate('/categories');
                }
              }}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 h-12"
              disabled={!selectedRoute || loading}
              data-testid="start-route-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {selectedRoute === 'A' 
                ? (learningPath ? 'Continue Adaptive Path' : 'Generate My Adaptive Path') 
                : selectedRoute === 'B' 
                ? 'Go to Practice Library' 
                : 'Select a Route'}
            </Button>
          </div>
          
          <p className="text-center text-gray-500 text-sm">
            You can switch routes anytime from your dashboard
          </p>
        </div>
      </div>
    );
  }

  // Lesson view
  if (view === 'lesson' && currentLesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setView('dashboard')}
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          <Card className="bg-slate-800/50 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-2xl text-white">{currentLesson.title}</CardTitle>
              <CardDescription className="text-gray-400">
                Estimated time: {currentLesson.estimated_time} minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={lessonTab} onValueChange={setLessonTab}>
                <TabsList className="grid w-full grid-cols-3 bg-slate-700/50">
                  <TabsTrigger value="learn">📚 Learn</TabsTrigger>
                  <TabsTrigger value="examples">💡 Examples</TabsTrigger>
                  <TabsTrigger value="practice">✏️ Practice</TabsTrigger>
                </TabsList>

                <TabsContent value="learn" className="mt-4">
                  <div className="prose prose-invert max-w-none">
                    <div className="bg-slate-700/50 rounded-lg p-6 text-gray-200 whitespace-pre-wrap">
                      {currentLesson.explanation}
                    </div>
                    
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold text-white mb-3">Key Concepts</h3>
                      <div className="grid gap-2">
                        {currentLesson.key_concepts.map((concept, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-slate-700/30 rounded p-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="text-gray-300">{concept}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="examples" className="mt-4 space-y-4">
                  {currentLesson.worked_examples.map((example, idx) => (
                    <div key={idx} className="bg-slate-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">{example.title}</h4>
                      <div className="bg-slate-800 rounded p-3 mb-3">
                        <span className="text-purple-400">Problem: </span>
                        <span className="text-white">{example.problem}</span>
                      </div>
                      <div className="space-y-2">
                        {example.steps.map((step, stepIdx) => (
                          <div key={stepIdx} className="flex items-start gap-2 text-gray-300">
                            <span className="text-purple-400 font-mono">{stepIdx + 1}.</span>
                            <span>{step}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-600">
                        <span className="text-green-400 font-semibold">Answer: </span>
                        <span className="text-white">{example.answer}</span>
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="practice" className="mt-4 space-y-4">
                  {currentLesson.practice_questions.map((q, idx) => (
                    <div key={idx} className="bg-slate-700/50 rounded-lg p-4">
                      <p className="text-white mb-3">
                        <span className="text-purple-400 font-semibold">Q{idx + 1}.</span> {q.question}
                      </p>
                      <RadioGroup 
                        value={practiceAnswers[idx]?.toString()} 
                        onValueChange={(v) => setPracticeAnswers({...practiceAnswers, [idx]: parseInt(v)})}
                        disabled={practiceSubmitted}
                        className="space-y-2"
                      >
                        {q.options.map((option, optIdx) => (
                          <div 
                            key={optIdx}
                            className={`flex items-center space-x-2 p-2 rounded transition-all
                              ${practiceSubmitted 
                                ? optIdx === q.correct_answer 
                                  ? 'bg-green-500/20 border border-green-500/50' 
                                  : practiceAnswers[idx] === optIdx 
                                    ? 'bg-red-500/20 border border-red-500/50'
                                    : 'bg-slate-600/30'
                                : practiceAnswers[idx] === optIdx
                                  ? 'bg-purple-500/20 border border-purple-500/50'
                                  : 'bg-slate-600/30 hover:bg-slate-600/50'
                              }`}
                          >
                            <RadioGroupItem value={optIdx.toString()} id={`q${idx}-opt${optIdx}`} />
                            <Label htmlFor={`q${idx}-opt${optIdx}`} className="text-gray-200 cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                      {practiceSubmitted && (
                        <div className="mt-3 pt-3 border-t border-slate-600">
                          <p className="text-sm text-gray-400">
                            <Lightbulb className="w-4 h-4 inline mr-1 text-yellow-400" />
                            {q.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}

                  {!practiceSubmitted ? (
                    <Button 
                      onClick={submitPractice}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                      disabled={Object.keys(practiceAnswers).length < currentLesson.practice_questions.length}
                    >
                      Submit Answers
                    </Button>
                  ) : (
                    <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-white mb-2">
                        Score: {practiceResults?.score}%
                      </p>
                      <p className="text-gray-300">{practiceResults?.message}</p>
                      
                      {/* Show AI Help button if score is low */}
                      {practiceResults?.score < 60 && (
                        <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <p className="text-yellow-400 text-sm mb-2">
                            <AlertCircle className="w-4 h-4 inline mr-1" />
                            Need extra help with this topic?
                          </p>
                          <Button 
                            onClick={() => {
                              // Collect mistakes for AI
                              const mistakes = currentLesson.practice_questions
                                .map((q, idx) => ({
                                  question: q.question,
                                  selected_answer: q.options[practiceAnswers[idx]] || 'Not answered',
                                  correct_answer: q.options[q.correct_answer]
                                }))
                                .filter((_, idx) => practiceAnswers[idx] !== currentLesson.practice_questions[idx].correct_answer);
                              
                              getAIExplanation(currentLesson.title, mistakes);
                            }}
                            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                            disabled={aiLoading}
                          >
                            {aiLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Sparkles className="w-4 h-4 mr-2" />
                            )}
                            Get AI Tutor Help
                          </Button>
                        </div>
                      )}
                      
                      <Button 
                        onClick={() => setView('dashboard')}
                        className="mt-4"
                        variant="outline"
                      >
                        Back to Dashboard
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Dashboard view
  if (view === 'dashboard' && learningPath) {
    const bandColor = BAND_COLORS[learningPath.current_band] || BAND_COLORS.secure;
    
    // Get current day for highlighting
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = days[new Date().getDay()];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
            </Button>
            <div className="flex items-center gap-2">
              {/* Route Indicator */}
              <Badge 
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 cursor-pointer hover:opacity-80"
                onClick={() => setView('weekly-summary')}
                data-testid="route-indicator"
              >
                <Brain className="w-3 h-3 mr-1" />
                Route A: Adaptive
              </Badge>
              <Badge className={`${bandColor.bg} ${bandColor.text}`}>
                Week {learningPath.week_number}
              </Badge>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className={`text-3xl font-bold ${bandColor.text}`}>
                  {learningPath.completion_percentage}%
                </div>
                <p className="text-gray-400 text-sm">Completed</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-white">
                  {learningPath.grade_forecast?.predicted_grade || 'B'}
                </div>
                <p className="text-gray-400 text-sm">Predicted Grade</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <div className="text-3xl font-bold text-green-400">
                  {learningPath.streak_days}
                </div>
                <p className="text-gray-400 text-sm">Day Streak</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4 text-center">
                <Badge className={`${bandColor.bg} ${bandColor.text}`}>
                  {learningPath.current_band}
                </Badge>
                <p className="text-gray-400 text-sm mt-1">Current Level</p>
              </CardContent>
            </Card>
          </div>

          {/* Grade Forecast Card */}
          <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                    Grade Forecast
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Based on your current progress and performance
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-white">
                    {learningPath.grade_forecast?.predicted_grade}
                  </div>
                  <p className="text-sm text-gray-400">
                    {learningPath.grade_forecast?.confidence}% confidence
                  </p>
                </div>
              </div>
              
              {/* Mock Score Integration */}
              {learningPath.last_mock_score && (
                <div className="mt-4 pt-4 border-t border-purple-500/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📝</span>
                    <div>
                      <p className="text-sm text-gray-400">Last Mock Exam</p>
                      <p className="text-lg font-bold text-white">{learningPath.last_mock_score}%</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/assessment-factory')}
                    className="text-purple-400 border-purple-400/50"
                  >
                    Take New Mock
                  </Button>
                </div>
              )}
              
              {learningPath.grade_forecast?.potential_grade !== learningPath.grade_forecast?.predicted_grade && (
                <div className="mt-4 pt-4 border-t border-purple-500/30">
                  <p className="text-purple-300 text-sm">
                    <Star className="w-4 h-4 inline mr-1" />
                    Complete all activities to potentially achieve <strong>{learningPath.grade_forecast?.potential_grade}</strong>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly Schedule */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                This Week's Learning Plan
              </CardTitle>
              <CardDescription className="text-gray-400">
                Daily micro-lessons + Friday mock exam
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue={currentDay === 'Saturday' || currentDay === 'Sunday' ? 'Monday' : currentDay}>
                <TabsList className="grid grid-cols-5 bg-slate-700/50">
                  {learningPath.weekly_schedule?.map((day) => {
                    const isToday = day.day === currentDay;
                    return (
                      <TabsTrigger 
                        key={day.day} 
                        value={day.day} 
                        className={`text-xs md:text-sm relative ${isToday ? 'text-purple-400 font-bold' : ''}`}
                        data-testid={`day-tab-${day.day.toLowerCase()}`}
                      >
                        {isToday && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                        )}
                        {day.day.slice(0, 3)}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {learningPath.weekly_schedule?.map((day) => {
                  const isToday = day.day === currentDay;
                  return (
                    <TabsContent key={day.day} value={day.day} className="mt-4 space-y-3">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          {isToday && <Badge className="bg-purple-500 text-white text-xs">Today</Badge>}
                          Focus: <span className="text-purple-400 capitalize">{day.primary_topic}</span>
                        </h4>
                        <span className="text-gray-400 text-sm">
                          <Clock className="w-4 h-4 inline mr-1" />
                        {day.total_time} min
                      </span>
                    </div>
                    
                    {day.activities.map((activity) => (
                      <div 
                        key={activity.id}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-all
                          ${activity.completed 
                            ? 'bg-green-500/10 border-green-500/30' 
                            : 'bg-slate-700/50 border-slate-600 hover:border-purple-500/50'}`}
                      >
                        <div className="flex items-center gap-3">
                          {activity.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-slate-500" />
                          )}
                          <div>
                            <p className="text-white font-medium">{activity.title}</p>
                            <p className="text-gray-400 text-sm">{activity.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-sm">{activity.estimated_time} min</span>
                          {!activity.completed && (
                            <Button 
                              size="sm"
                              onClick={() => {
                                if (activity.type === 'mock') {
                                  // Use Adaptive Mock Engine for personalized mocks
                                  navigate('/adaptive-mock');
                                } else if (activity.type === 'learn' || activity.type === 'practice') {
                                  openLesson(activity.lesson_id);
                                } else {
                                  markActivityComplete(activity.id);
                                }
                              }}
                              className={activity.type === 'mock' 
                                ? "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600" 
                                : "bg-purple-500 hover:bg-purple-600"}
                            >
                              {activity.type === 'mock' ? '🧠' : <Play className="w-4 h-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                  );
                })}
              </Tabs>
            </CardContent>
          </Card>

          {/* Weekly Summary & Next Week Planning - NEW */}
          <Card className="bg-gradient-to-r from-indigo-500/20 to-blue-500/20 border-indigo-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-indigo-500/20 rounded-xl">
                    <RefreshCw className="w-6 h-6 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      Weekly Loop Closure
                    </h3>
                    <p className="text-gray-400 text-sm">
                      Review your progress and plan ahead with the Repopl Engine
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => setView('weekly-summary')}
                  className="bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600"
                  data-testid="weekly-summary-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Weekly Summary
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Tutor - Get Help on Struggling Topics */}
          {strugglingTopics.length > 0 && (
            <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  AI Tutor Available
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Get personalized explanations for topics you're struggling with
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {strugglingTopics.slice(0, 3).map((item) => (
                    <div 
                      key={item.topic}
                      className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                    >
                      <div>
                        <p className="text-white font-medium">{item.topic}</p>
                        <p className="text-sm text-gray-400">
                          Average score: <span className="text-red-400">{item.avg_score}%</span> • {item.count} attempts
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => getAIExplanation(item.topic, [])}
                        className="bg-purple-500 hover:bg-purple-600"
                        disabled={aiLoading}
                      >
                        {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />}
                        Get Help
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Focus Areas */}
          {learningPath.weak_topics?.length > 0 && (
            <Card className="bg-slate-800/50 border-red-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-red-400" />
                  Focus Areas
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Topics that need extra attention
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {learningPath.weak_topics.map((topic) => (
                    <Badge 
                      key={topic} 
                      variant="outline" 
                      className="text-red-400 border-red-400/50 capitalize cursor-pointer hover:bg-red-500/20"
                      onClick={() => getAIExplanation(topic, [])}
                    >
                      {topic}
                      <Sparkles className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Click any topic for AI-powered explanation
                </p>
              </CardContent>
            </Card>
          )}

          {/* Route Switch Option */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Route className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-white font-medium">Currently on: Adaptive Path (Route A)</p>
                  <p className="text-gray-400 text-sm">Switch to Practice Library for self-directed study</p>
                </div>
              </div>
              <Button 
                variant="outline"
                onClick={() => {
                  // Go back to route selection view
                  setView('route-choice');
                  toast.info('Select your preferred learning route');
                }}
                className="text-blue-400 border-blue-400/50 hover:bg-blue-500/10"
                data-testid="switch-route-btn"
              >
                <Library className="w-4 h-4 mr-2" />
                Switch Route
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Weekly Summary View - NEW
  if (view === 'weekly-summary' && learningPath) {
    const bandColor = BAND_COLORS[learningPath.current_band] || BAND_COLORS.secure;
    const completedActivities = learningPath.weekly_schedule?.reduce((acc, day) => 
      acc + (day.activities?.filter(a => a.completed).length || 0), 0) || 0;
    const totalActivities = learningPath.weekly_schedule?.reduce((acc, day) => 
      acc + (day.activities?.length || 0), 0) || 0;
    const completionRate = totalActivities > 0 ? Math.round((completedActivities / totalActivities) * 100) : 0;
    
    // Get current day of week
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = days[new Date().getDay()];
    const isFriday = currentDay === 'Friday';
    const isWeekend = currentDay === 'Saturday' || currentDay === 'Sunday';
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => setView('dashboard')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-white flex items-center justify-center gap-3">
              <RefreshCw className="w-8 h-8 text-indigo-400" />
              Weekly Summary
            </h1>
            <p className="text-gray-400">Week {learningPath.week_number} Progress & Planning</p>
          </div>

          {/* Week Status Banner */}
          {(isFriday || isWeekend) && (
            <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Calendar className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {isFriday ? "It's Friday - Perfect time to wrap up your week!" : "Weekend Review Time!"}
                  </p>
                  <p className="text-gray-400 text-sm">
                    Complete any remaining activities and plan for next week
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress Overview */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                Week {learningPath.week_number} Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Completion Rate */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Activities Completed</span>
                  <span className="text-white font-medium">{completedActivities}/{totalActivities}</span>
                </div>
                <Progress value={completionRate} className="h-3" />
                <p className="text-center text-lg font-bold" style={{color: completionRate >= 80 ? '#4ade80' : completionRate >= 50 ? '#fbbf24' : '#f87171'}}>
                  {completionRate}% Complete
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-white">{learningPath.streak_days}</div>
                  <p className="text-gray-400 text-xs mt-1">Day Streak</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className={`text-2xl font-bold ${bandColor.text}`}>{learningPath.current_band}</div>
                  <p className="text-gray-400 text-xs mt-1 capitalize">Level</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">{learningPath.grade_forecast?.predicted_grade || 'B'}</div>
                  <p className="text-gray-400 text-xs mt-1">Predicted</p>
                </div>
              </div>

              {/* Daily Breakdown */}
              <div className="space-y-3">
                <h4 className="text-white font-medium">Daily Progress</h4>
                {learningPath.weekly_schedule?.map((day) => {
                  const dayCompleted = day.activities?.filter(a => a.completed).length || 0;
                  const dayTotal = day.activities?.length || 0;
                  const dayPercent = dayTotal > 0 ? Math.round((dayCompleted / dayTotal) * 100) : 0;
                  const isToday = day.day === currentDay;
                  
                  return (
                    <div 
                      key={day.day} 
                      className={`flex items-center gap-4 p-3 rounded-lg ${
                        isToday ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-slate-700/30'
                      }`}
                    >
                      <div className="w-20 text-sm font-medium text-gray-300 flex items-center gap-2">
                        {isToday && <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />}
                        {day.day.slice(0, 3)}
                      </div>
                      <div className="flex-1">
                        <Progress value={dayPercent} className="h-2" />
                      </div>
                      <div className="text-sm text-gray-400 w-16 text-right">
                        {dayCompleted}/{dayTotal}
                      </div>
                      {dayPercent === 100 && (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Focus Areas & Improvements */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Strengths */}
            <Card className="bg-green-500/10 border-green-500/30">
              <CardHeader>
                <CardTitle className="text-green-400 flex items-center gap-2 text-lg">
                  <CheckCircle2 className="w-5 h-5" />
                  Strengths This Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {learningPath.strong_topics?.length > 0 ? (
                    learningPath.strong_topics.map((topic, idx) => (
                      <Badge key={idx} className="mr-2 bg-green-500/20 text-green-400 capitalize">
                        {topic}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">Keep practicing to identify your strengths!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Areas to Focus */}
            <Card className="bg-red-500/10 border-red-500/30">
              <CardHeader>
                <CardTitle className="text-red-400 flex items-center gap-2 text-lg">
                  <AlertCircle className="w-5 h-5" />
                  Focus for Next Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {learningPath.weak_topics?.length > 0 ? (
                    learningPath.weak_topics.map((topic, idx) => (
                      <Badge key={idx} className="mr-2 bg-red-500/20 text-red-400 capitalize">
                        {topic}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">Great job! No major weak areas detected.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Repopl Engine - Plan Next Week */}
          <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Repopl Optimisation Engine
              </CardTitle>
              <CardDescription className="text-gray-400">
                AI-powered weekly plan recalibration based on your progress
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <span className="text-white font-medium">Next Week's Focus:</span>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  {learningPath.weak_topics?.length > 0 ? (
                    <>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-purple-400" />
                        Reinforced practice on: {learningPath.weak_topics.slice(0, 2).join(', ')}
                      </li>
                      <li className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-purple-400" />
                        Adjusted difficulty based on your {bandColor.text?.split('-')[1]} level
                      </li>
                    </>
                  ) : (
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-purple-400" />
                      Advancing to more challenging content
                    </li>
                  )}
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-purple-400" />
                    Friday mock exam scheduled to track improvement
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={async () => {
                    setLoading(true);
                    await generateLearningPath();
                    setView('dashboard');
                    toast.success('Week plan regenerated with Repopl Engine!');
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  disabled={loading}
                  data-testid="regenerate-plan-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Regenerate Next Week's Plan
                </Button>
                <Button 
                  onClick={() => navigate(`/assessment-factory?stage=${stage}&subject=${subject}`)}
                  variant="outline"
                  className="flex-1 text-purple-400 border-purple-400/50 hover:bg-purple-500/10"
                  data-testid="take-mock-exam-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Take Mock Exam
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Fallback loading
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
    </div>
  );
};

export default LearningEngine;
